export const PRACTICE_KINDS = [
  "adult_gp",
  "pediatric_gp",
  "mixed",
  "other",
] as const;

export type PracticeKind = (typeof PRACTICE_KINDS)[number];

export const PRACTICE_KIND_LABELS: Record<PracticeKind, string> = {
  adult_gp: "Felnőtt háziorvosi praxis",
  pediatric_gp: "Házi gyermekorvosi praxis",
  mixed: "Vegyes praxis",
  other: "Egyéb",
};

export const PRACTICE_PROFESSIONAL_ROLES = [
  "physician",
  "nurse",
  "assistant",
  "manager",
  "other",
] as const;

export type PracticeProfessionalRole =
  (typeof PRACTICE_PROFESSIONAL_ROLES)[number];

export const PRACTICE_PROFESSIONAL_ROLE_LABELS: Record<
  PracticeProfessionalRole,
  string
> = {
  physician: "Orvos",
  nurse: "Szakdolgozó",
  assistant: "Asszisztens",
  manager: "Praxisvezető",
  other: "Egyéb",
};

export const PRACTICE_ACCESS_ROLES = ["member", "practice_admin"] as const;

export type PracticeAccessRole = (typeof PRACTICE_ACCESS_ROLES)[number];

export const PRACTICE_ACCESS_ROLE_LABELS: Record<PracticeAccessRole, string> = {
  member: "Tag",
  practice_admin: "Praxisadmin",
};

export const PRACTICE_MEMBERSHIP_STATUSES = [
  "pending",
  "active",
  "declined",
  "banned",
  "left",
] as const;

export type PracticeMembershipStatus =
  (typeof PRACTICE_MEMBERSHIP_STATUSES)[number];

export const PRACTICE_MEMBERSHIP_STATUS_LABELS: Record<
  PracticeMembershipStatus,
  string
> = {
  pending: "Függőben",
  active: "Aktív",
  declined: "Elutasítva",
  banned: "Tiltva",
  left: "Kilépett",
};

export const PRACTICE_JOIN_SOURCES = ["created", "invite", "request"] as const;

export type PracticeJoinSource = (typeof PRACTICE_JOIN_SOURCES)[number];

export function isPracticeKind(value: string): value is PracticeKind {
  return PRACTICE_KINDS.includes(value as PracticeKind);
}

export function isProfessionalRole(
  value: string,
): value is PracticeProfessionalRole {
  return PRACTICE_PROFESSIONAL_ROLES.includes(
    value as PracticeProfessionalRole,
  );
}

export function isAccessRole(value: string): value is PracticeAccessRole {
  return PRACTICE_ACCESS_ROLES.includes(value as PracticeAccessRole);
}

export function isMembershipStatus(
  value: string,
): value is PracticeMembershipStatus {
  return PRACTICE_MEMBERSHIP_STATUSES.includes(
    value as PracticeMembershipStatus,
  );
}

export function isJoinSource(value: string): value is PracticeJoinSource {
  return PRACTICE_JOIN_SOURCES.includes(value as PracticeJoinSource);
}

export type PracticeStatus = "active" | "inactive" | "archived";

export const PRACTICE_STATUS_LABELS: Record<PracticeStatus, string> = {
  active: "Aktív",
  inactive: "Inaktív",
  archived: "Archivált",
};

export function practiceStatus(input: {
  isActive: boolean;
  archivedAt?: string | null;
}): PracticeStatus {
  if (input.archivedAt) {
    return "archived";
  }
  return input.isActive ? "active" : "inactive";
}

export type PracticeMembership = {
  professionalRole: PracticeProfessionalRole;
  accessRole: PracticeAccessRole;
  status: PracticeMembershipStatus;
  isPrimary: boolean;
  joinSource: PracticeJoinSource;
};

export type MyPractice = {
  id: string;
  name: string;
  kind: PracticeKind;
  city: string;
  addressLine: string | null;
  postalCode: string | null;
  isActive: boolean;
  memberCount: number;
  professionalRole: PracticeProfessionalRole;
  accessRole: PracticeAccessRole;
  membershipStatus: PracticeMembershipStatus;
  isPrimary: boolean;
  joinSource: PracticeJoinSource;
  invitedByName: string | null;
  createdAt: string;
};

export type PracticeSearchHit = {
  id: string;
  name: string;
  kind: PracticeKind;
  city: string;
  memberCount: number;
  membershipStatus: PracticeMembershipStatus | null;
};

export type PracticeDetail = {
  id: string;
  name: string;
  kind: PracticeKind;
  city: string;
  memberCount: number;
  isActive: boolean;
  archivedAt: string | null;
  createdAt: string | null;
  canManage: boolean;
  canArchive: boolean;
  isMember: boolean;
  publicOnly: boolean;
  membership: PracticeMembership | null;
  postalCode: string | null;
  addressLine: string | null;
  phone: string | null;
  email: string | null;
  summary: string | null;
  externalIdentifier: string | null;
  contactUserId: string | null;
};

export type PracticeMember = {
  id: string;
  userId: string;
  fullName: string;
  professionalTitle: string | null;
  avatarPath: string | null;
  professionalRole: PracticeProfessionalRole;
  accessRole: PracticeAccessRole;
  status: PracticeMembershipStatus;
  isPrimary: boolean;
  joinSource: PracticeJoinSource;
  invitedBy: string | null;
  invitedByName: string | null;
  joinedAt: string | null;
  createdAt: string;
};

export type PracticeInviteCandidate = {
  id: string;
  fullName: string;
  professionalTitle: string | null;
  avatarPath: string | null;
};

export type PracticeServiceRequest = {
  id: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  serviceName: string;
  serviceSlug: string;
  userId: string;
  userName: string;
  offerStatus: string | null;
  offerSummary: string | null;
};

export type ProfilePractice = {
  id: string;
  name: string;
  kind: PracticeKind;
  city: string;
  isPrimary: boolean;
  canOpen: boolean;
};

export type PracticeCreateInput = {
  name: string;
  kind: PracticeKind;
  city: string;
  postalCode?: string;
  addressLine?: string;
  phone?: string;
  email?: string;
  identifier?: string;
  summary?: string;
};

export type PracticeUpdateInput = PracticeCreateInput;

export type AdminPracticeListItem = {
  id: string;
  name: string;
  kind: PracticeKind;
  city: string;
  isActive: boolean;
  archivedAt: string | null;
  memberCount: number;
  createdAt: string;
  activeRequestCount: number;
};

export type AdminPracticeMember = {
  userId: string;
  fullName: string;
  professionalRole: PracticeProfessionalRole;
  accessRole: PracticeAccessRole;
  status: PracticeMembershipStatus;
  isPrimary: boolean;
};

export type AdminPracticeLink = {
  id: string;
  status: string;
  createdAt: string;
  serviceName: string;
  userName: string;
};

export type AdminPracticeOffer = {
  id: string;
  status: string;
  summary: string;
  amount: number | null;
  currency: string;
  createdAt: string;
  leadId: string;
};

export type AdminPracticeDetail = {
  id: string;
  name: string;
  kind: PracticeKind;
  city: string;
  postalCode: string | null;
  addressLine: string | null;
  phone: string | null;
  email: string | null;
  summary: string | null;
  externalIdentifier: string | null;
  isActive: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  memberCount: number;
  members: AdminPracticeMember[];
  serviceRequests: AdminPracticeLink[];
  leads: AdminPracticeLink[];
  offers: AdminPracticeOffer[];
};

export type PublicProfile = {
  id: string;
  fullName: string;
  professionalTitle: string | null;
  occupation: string | null;
  city: string | null;
  bio: string | null;
  avatarPath: string | null;
};
